/**
 * The portal's own vocabulary, shared by the API and the browser.
 *
 * Two kinds of thing are tracked, and they must never be confused:
 *
 *   the RECORD   every farm in the programme villages and who holds it, from
 *                the state revenue record (built offline by tools/build_roster.py;
 *                read-only here)
 *   ONBOARDING   how far each holder has come on WhatsApp - partly what field
 *                staff enter here (a phone number, an invitation sent), partly
 *                what the AI Bhoomi bot recorded (a pin, a boundary, activation)
 *
 * Every date is an ISO string or null. Nothing here is estimated: a stage is
 * reached only when something recorded it.
 */

export type Segment = 'marginal' | 'small' | 'medium';

export const SEGMENT_LABEL: Record<Segment, string> = {
  marginal: 'Marginal — holder under 1 ha',
  small: 'Small — holder 1 to 2 ha',
  medium: 'Medium — holder 2 ha and over',
};

/** Colour per segment, as on the AI Bhoomi farm map. */
export const SEGMENT_COLOR: Record<Segment, string> = {
  marginal: '#f2b705',
  small: '#4ea8de',
  medium: '#7048e8',
};

/**
 * The onboarding stages, in order. The first three are entered by staff; the
 * rest mirror the bot's own state machine (new -> awaiting_location ->
 * awaiting_source -> awaiting_polygon -> awaiting_language -> awaiting_confirm
 * -> activated) so the portal and the bot never disagree about where a
 * farmer stands.
 */
export type Stage = 'target' | 'phone' | 'invited' | 'joined' | 'pin' | 'boundary' | 'confirmed' | 'activated';

export const STAGES: Stage[] = ['target', 'phone', 'invited', 'joined', 'pin', 'boundary', 'confirmed', 'activated'];

export const STAGE_LABEL: Record<Stage, string> = {
  target: 'On the record',
  phone: 'Phone captured',
  invited: 'Invited on WhatsApp',
  joined: 'Wrote to the bot',
  pin: 'Pin shared',
  boundary: 'Boundary marked',
  confirmed: 'Profile confirmed',
  activated: 'Activated',
};

export const STAGE_HINT: Record<Stage, string> = {
  target: 'Named on the khasra record; nobody has reached them yet.',
  phone: 'A field officer recorded a mobile number for this holder.',
  invited: 'The WhatsApp link or QR was sent or shown to the farmer.',
  joined: 'The farmer sent a first message; the bot asked for a location.',
  pin: 'A location pin arrived. It is checked against the khasra boundary.',
  boundary: 'A field boundary exists: drawn by the farmer, or found by the field desk.',
  confirmed: 'Language chosen and the profile card confirmed.',
  activated: 'The bot is in normal conversation; reports and alerts flow.',
};

/** Colour per stage. Grey for untouched, warm for staff steps, green ramp for the bot's. */
export const STAGE_COLOR: Record<Stage, string> = {
  target: '#4b5a61',
  phone: '#b08a3e',
  invited: '#e0a526',
  joined: '#5aa9e6',
  pin: '#3f8fd6',
  boundary: '#2fb08a',
  confirmed: '#36c98f',
  activated: '#4edea3',
};

export const stageIndex = (s: Stage): number => STAGES.indexOf(s);

export interface HolderFarm {
  pid: string;
  khasra: string;
  acres: number;
  /** [lat, lon], always inside this farm's own boundary. */
  pin: [number, number];
  shared: boolean;
  in_block: boolean;
  co_owners: string[];
}

/** One farmer (khatedar) on the revenue record. */
export interface Holder {
  id: string;
  name: string;
  /** "पिता X" / "पति Y" as the record states, or empty. */
  relation: string;
  village: string;
  farm_count: number;
  /** Sum of the recorded areas of the khasras this holder is named on. */
  acres: number;
  segment: Segment | null;
  segment_source: 'sole' | 'shared' | null;
  size_class: string | null;
  holding_acres: number | null;
  holding_parcels: number | null;
  crop_last_season: string | null;
  in_block: boolean;
  primary_pid: string;
  /** [lat, lon] of the largest farm, inside it. */
  pin: [number, number];
  farms: HolderFarm[];
}

export type Season = 'kharif' | 'rabi' | 'zaid';
export const SEASONS: { id: Season; label: string; months: string }[] = [
  { id: 'kharif', label: 'Kharif', months: 'Jun–Oct' },
  { id: 'rabi', label: 'Rabi', months: 'Oct–Mar' },
  { id: 'zaid', label: 'Zaid / summer', months: 'Mar–Jun' },
];

/** What a farmer grows: one entry per crop per season, optionally on named khasras only. */
export interface CropEntry {
  season: Season;
  crop: string;
  khasras?: string[];
}

/** The crops grown around Neemuch (Malwa plateau), Kharif then Rabi then summer. */
export const CROPS: Record<Season, string[]> = {
  kharif: ['Soybean', 'Maize', 'Urad', 'Moong', 'Groundnut', 'Cotton', 'Sorghum (jowar)', 'Sesame', 'Pigeonpea (arhar)', 'Vegetables'],
  rabi: ['Wheat', 'Garlic', 'Gram (chana)', 'Mustard', 'Coriander', 'Opium poppy', 'Onion', 'Fenugreek (methi)', 'Isabgol', 'Lentil (masoor)', 'Pea', 'Barley', 'Linseed', 'Ajwain'],
  zaid: ['Moong', 'Vegetables', 'Fodder', 'Onion', 'Watermelon / muskmelon'],
};

export interface OnboardingRow {
  holder_id: string;
  /** JSON of CropEntry[], or null when nobody has recorded the crops yet. */
  crops: string | null;
  phone: string | null;
  wa_id: string | null;
  stage: Stage;
  /** The bot's own state word, when the bot has seen this farmer. */
  bot_state: string | null;
  bot_name: string | null;
  assigned_to: string | null;
  notes: string | null;
  pin_lat: number | null;
  pin_lon: number | null;
  /** The khasra the pin fell in, or null when it fell outside every farm. */
  pin_pid: string | null;
  /** True when the pin fell in one of THIS holder's own khasras. */
  pin_inside_own: boolean;
  /** Metres from the pin to the nearest farm when it fell outside all of them. */
  pin_distance_m: number | null;
  /** [[lat, lon], ...] of the marked boundary, JSON. */
  boundary_json: string | null;
  boundary_source: string | null;
  boundary_acres: number | null;
  phone_at: string | null;
  invited_at: string | null;
  joined_at: string | null;
  pin_at: string | null;
  boundary_at: string | null;
  activated_at: string | null;
  last_message_at: string | null;
  messages: number;
  updated_at: string;
  updated_by: string | null;
}

/** A bot farmer whose pin fell outside every farm in the record. */
export interface UnmatchedPin {
  wa_id: string;
  name: string;
  bot_state: string;
  lat: number;
  lon: number;
  nearest_pid: string | null;
  nearest_khasra: string | null;
  nearest_village: string | null;
  distance_m: number | null;
  created_at: string;
  last_message_at: string | null;
  /**
   * Set by the server when this bot farmer is not on the programme's own list:
   * they are AI Bhoomi's farmer, not UltraTech's, so name is '' and wa_id is
   * only "…" plus the last four digits.
   */
  masked?: boolean;
}

export interface OnboardingEvent {
  id: number;
  holder_id: string | null;
  kind: string;
  detail: string;
  by: string;
  at: string;
}

export interface Staff {
  email: string;
  name: string;
  role: 'admin' | 'officer';
  villages: string[];
  created_at: string;
}

export interface SyncRun {
  at: string;
  by: string;
  source: string;
  farmers_read: number;
  matched: number;
  outside: number;
  note: string | null;
}

export interface RosterSummary {
  generated_from: string;
  villages_mapped: string[];
  villages_missing_from_extract: string[];
  farms: number;
  farm_acres: number;
  farms_in_block: number;
  farms_unnamed: number;
  holders: number;
  holders_in_block: number;
  holder_segments: Record<string, { label: string; holders: number; acres: number }>;
  pin_method: Record<string, number>;
  pin_rule: string;
  holder_rule: string;
  per_village: Record<string, { farms: number; holders: number; acres: number; segments: Record<string, number>; in_block_farms: number; unnamed_farms: number }>;
}

export interface Brand {
  product: string;
  poweredBy: string;
  customer: string;
  customerGroup: string;
  programme: string;
  owner: string;
  ownerShort: string;
  contractRef: string;
}

/** Who this deployment is for. One customer, so it is a constant, not a tenant table. */
export const BRAND: Brand = {
  product: 'UltraTech Farmer Portal',
  poweredBy: 'AI Bhoomi · rurtech.ai',
  customer: 'UltraTech Cement',
  customerGroup: 'Aditya Birla Group',
  programme: 'Vikram Cement Works, Neemuch (M.P.) — farmer programme',
  owner: 'RurSat Spectral Research Labs Pvt Ltd',
  ownerShort: 'RurSat Spectral Research Labs',
  contractRef: 'RURTECH/UTCL-VCW/2026-09/01',
};

export const prettyPhone = (n: string | null | undefined): string => {
  if (!n) return '';
  const d = n.replace(/\D/g, '');
  return d.startsWith('91') && d.length === 12 ? `+91 ${d.slice(2, 7)} ${d.slice(7)}` : `+${d}`;
};
